import React, { useEffect, useState } from "react";
import axios from "axios";
import "./RulesConfigurations.css";
import ConfirmationDialog from "components/utils/ConfirmationDialog";
import openNotification from "components/utils/CustomNotification";
import { useCheckSessionHeartBeat } from "components/API/ApiEnpoints";

interface RuleCondition {
  operator: string;
  value: number;
}

interface RulesConfig {
  org_uid: string;
  rule_type: string;
  rule_sub_type: string;
  condition_type: string;
  condition: RuleCondition[];
  rule_uid?: string;
}

interface OrgInfo {
  org_uid: string;
  org_name?: string;
}

const RulesConfigurations: React.FC = () => {
  useCheckSessionHeartBeat();
  const [orgUid, setOrgUid] = useState<string>("");
  const [orgList, setOrgList] = useState<OrgInfo[]>([]);
  const [ruleType, setRuleType] = useState<string>("");
  const [ruleSubType, setRuleSubType] = useState<string>("");
  const [conditionType, setConditionType] = useState<string>("");
  const [operator, setOperator] = useState<string>("");
  const [value, setValue] = useState<string>("");
  const [rules, setRules] = useState<RulesConfig[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [deleteRule, setDeleteRule] = useState<RulesConfig | null>(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const beAddress =
    window._env_?.REACT_APP_API_URL || process.env.REACT_APP_API_URL;
  const sessionUid = localStorage.getItem("session_uid") || "";
  const userUid = localStorage.getItem("user_uid") || "";

  useEffect(() => {
    const fetchOrg = async () => {
      try {
        const response = await axios.get(`${beAddress}/api/org-info`, {
          headers: {
            "user-uid": userUid,
          },
          withCredentials: true,
        });

        if (response.status === 200 && response.data.data?.length > 0) {
          const orgs = response.data.data;
          setOrgList(orgs);
          setOrgUid(orgs[0].org_uid);
        }
      } catch (err) {
        console.error("Error fetching org info:", err);
      }
    };

    fetchOrg();
  }, [beAddress, sessionUid, userUid]);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${beAddress}/api/rules-config`, {
        headers: { "user-uid": userUid },
        withCredentials: true,
      });
      if (res.data?.data) setRules(res.data.data);
      else setRules([]);
    } catch (error) {
      console.error("Error fetching rules:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRules();
  }, []);

  const handleAddRule = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !orgUid ||
      !ruleType ||
      !ruleSubType ||
      !conditionType ||
      !operator ||
      !value
    ) {
      openNotification({
        type: "error",
        message: "All fields are required!",
      });
      return;
    }

    setIsSubmitting(true);
    const payload: RulesConfig[] = [
      {
        org_uid: orgUid,
        rule_type: ruleType,
        rule_sub_type: ruleSubType,
        condition_type: conditionType,
        condition: [{ operator, value: parseFloat(value) }],
      },
    ];

    try {
      const res = await axios.post(`${beAddress}/api/rules-config`, payload, {
        headers: { "user-uid": userUid },
        withCredentials: true,
      });

      if (res.status === 200 && res.data?.status === "Success") {
        openNotification({
          type: "success",
          message: res.data.message,
        });
        setRuleType("");
        setRuleSubType("");
        setConditionType("");
        setOperator("");
        setValue("");
        setIsModalOpen(false);
        fetchRules();
      } else {
        openNotification({
          type: "error",
          message: res.data?.message || "Failed to add rule",
        });
      }
    } catch (err: any) {
      console.error("Error adding rule:", err);
      openNotification({
        type: "error",
        message: err.response?.data?.data || "Failed to add rule",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRule = async (rule: RulesConfig) => {
    if (!rule?.rule_uid) return;

    try {
      const res = await axios.delete(`${beAddress}/api/rules-config`, {
        headers: { "user-uid": userUid },
        withCredentials: true,
        data: [
          {
            org_uid: rule.org_uid,
            rule_type: rule.rule_type,
            rule_sub_type: rule.rule_sub_type,
            condition_type: rule.condition_type,
            condition: rule.condition,
            rule_uid: rule.rule_uid,
          },
        ],
      });

      if (res.status === 200 && res.data?.status === "Success") {
        openNotification({
          type: "success",
          message: res.data.message,
        });
        fetchRules();
      } else {
        openNotification({
          type: "error",
          message: res.data?.message || "Failed to delete rule",
        });
      }
    } catch (err: any) {
      console.error("Error deleting rule:", err);
      openNotification({
        type: "error",
        message: err.response?.data?.data || "Failed to delete rule",
      });
    }
  };

  return (
    <div className="ruleconfigurations-container">
      <div className="ruleconfigurations-header">
        <h2>Rules Configuration</h2>

        <button
          className="ruleconfigurations-add-button"
          onClick={() => setIsModalOpen(true)}
        >
          Add New Rule
        </button>
      </div>
      {isModalOpen && (
        <div className="ruleconfigurations-modal-overlay">
          <div className="ruleconfigurations-modal">
            <h3>Add New Rule</h3>
            <form className="ruleconfigurations-form" onSubmit={handleAddRule}>
              <div className="ruleconfigurations-form-row">
                <label>Org UID:</label>
                <select
                  value={orgUid}
                  onChange={(e) => setOrgUid(e.target.value)}
                >
                  <option value="">Select Org</option>
                  {orgList.map((org) => (
                    <option key={org.org_uid} value={org.org_uid}>
                      {org.org_name || org.org_uid}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ruleconfigurations-form-row">
                <label>Rule Type:</label>
                <select
                  value={ruleType}
                  onChange={(e) => setRuleType(e.target.value)}
                >
                  <option value="">Select Type</option>
                  <option value="badge">Badges</option>
                  {/* <option value="rules">Rules</option> */}
                </select>
              </div>

              <div className="ruleconfigurations-form-row">
                <label>Rule Sub Type:</label>
                <select
                  value={ruleSubType}
                  onChange={(e) => setRuleSubType(e.target.value)}
                >
                  <option value="">Select Sub Type</option>
                  <option value="steps">Steps</option>
                  <option value="distance">Distance</option>
                  <option value="caloriesburned">Calories</option>
                </select>
              </div>

              <div className="ruleconfigurations-form-row">
                <label>Condition Type:</label>
                <select
                  value={conditionType}
                  onChange={(e) => setConditionType(e.target.value)}
                >
                  <option value="">Select Condition Type</option>
                  <option value="daily">Daily</option>
                  <option value="aggregate">Aggregate</option>
                </select>
              </div>

              <div className="ruleconfigurations-form-row">
                <label>Operator:</label>
                <select
                  value={operator}
                  onChange={(e) => setOperator(e.target.value)}
                >
                  <option value="">Select Operator</option>
                  <option value="gt">Greater Than</option>
                  <option value="gte">Greater Than Equal To</option>
                  <option value="lt">Less Than</option>
                  <option value="lte">Less Than Equal To</option>
                  <option value="eq">Equal To</option>
                </select>
              </div>

              <div className="ruleconfigurations-form-row">
                <label>Value:</label>
                <input
                  type="number"
                  min="0"
                  value={value}
                  onChange={(e) => {
                    if (e.target.value.length <= 12) {
                      setValue(e.target.value);
                    }
                  }}
                />
              </div>

              <div className="ruleconfigurations-modal-actions">
                <button
                  type="submit"
                  className="ruleconfigurations-submit-button"
                  disabled={isSubmitting}
                  style={{
                    opacity: isSubmitting ? 0.6 : 1,
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                  }}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </button>

                <button
                  type="button"
                  className="ruleconfigurations-cancel-button"
                  onClick={() => {
                    setIsModalOpen(false);
                    setRuleType("");
                    setRuleSubType("");
                    setConditionType("");
                    setOperator("");
                    setValue("");
                  }}
                  disabled={isSubmitting}
                  style={{
                    opacity: isSubmitting ? 0.6 : 1,
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* <h3>Existing Rules</h3> */}
      {loading ? (
        <p>Loading...</p>
      ) : (
        <table className="ruleconfigurations-table">
          <thead>
            <tr>
              <th>Org UID</th>
              <th>Rule Type</th>
              <th>Rule Sub Type</th>
              <th>Condition Type</th>
              <th>Operator</th>
              <th>Value</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {rules.length > 0 ? (
              rules.map((r, index) => (
                <tr key={index}>
                  <td>{r.org_uid}</td>
                  <td>{r.rule_type}</td>
                  <td>{r.rule_sub_type}</td>
                  <td>{r.condition_type}</td>
                  <td>{r.condition?.[0]?.operator}</td>
                  <td>{r.condition?.[0]?.value}</td>
                  <td>
                    <button
                      onClick={() => {
                        setDeleteRule(r);
                        setIsConfirmOpen(true);
                      }}
                      style={{
                        backgroundColor: "rgb(188, 26, 26)",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        padding: "8px 16px",
                        cursor: "pointer",
                      }}
                    >
                      Delete
                    </button>

                    <ConfirmationDialog
                      show={
                        isConfirmOpen && deleteRule?.rule_uid === r.rule_uid
                      }
                      message="Are you sure you want to delete this rule?"
                      onConfirm={() => {
                        if (deleteRule) handleDeleteRule(deleteRule);
                        setIsConfirmOpen(false);
                        setDeleteRule(null);
                      }}
                      onCancel={() => {
                        setIsConfirmOpen(false);
                        setDeleteRule(null);
                      }}
                      onClose={() => {
                        setIsConfirmOpen(false);
                        setDeleteRule(null);
                      }}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} style={{ textAlign: "center" }}>
                  No rules found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default RulesConfigurations;
